import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { usePhotoUrl } from '@/hooks/use-photo'

const PALETTE = [
    'bg-sky-100 text-sky-800',
    'bg-emerald-100 text-emerald-800',
    'bg-violet-100 text-violet-800',
    'bg-amber-100 text-amber-900',
    'bg-rose-100 text-rose-800',
    'bg-teal-100 text-teal-800',
    'bg-indigo-100 text-indigo-800',
    'bg-orange-100 text-orange-800'
]

const SIZES = {
    xs: 'size-6 text-[10px]',
    sm: 'size-8 text-xs',
    md: 'size-10 text-sm',
    lg: 'size-12 text-base',
    xl: 'size-16 text-xl'
}

export const initialsOf = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

const colorFor = (name: string): string => {
    let hash = 0
    for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) >>> 0
    return PALETTE[hash % PALETTE.length]
}

interface AvatarProps {
    name: string
    photo?: string | null
    size?: keyof typeof SIZES
    className?: string
}

export const Avatar: FC<AvatarProps> = ({
    name,
    photo,
    size = 'md',
    className
}): ReactNode => {
    const url = usePhotoUrl(photo)

    return (
        <span
            className={cn(
                'relative inline-grid shrink-0 place-items-center overflow-hidden rounded-full font-heading font-bold select-none',
                SIZES[size],
                url ? 'bg-slate-100' : colorFor(name),
                className
            )}
            aria-hidden='true'
        >
            {url
                ? <img src={url} alt='' className='size-full object-cover' />
                : initialsOf(name)}
        </span>
    )
}
